import { FormEvent, useMemo, useState } from 'react'
import { Check, ChevronDown, MessageCircle, Minus, Plus, ReceiptText, Search, Send, Sparkles, UsersRound, UtensilsCrossed, X } from 'lucide-react'
import { useParams } from 'react-router-dom'
import { StatusPill, elapsedSince } from '../components/StatusPill'
import { useLamsa } from '../context/LamsaContext'
import type { MenuItem, ServiceRequest } from '../types'

export default function GuestPage() {
  const { tableNumber = '12' } = useParams()
  const tableNo = Number(tableNumber)
  const {
    restaurant, categories, menuItems, cartItems, orders, requests, messages, guestSession,
    ensureGuestSession, addToCart, changeCartQty, submitOrder, createServiceRequest, sendMessage, backendMode,
  } = useLamsa()
  const [guestName, setGuestName] = useState('Guest 1')
  const [activeCategory, setActiveCategory] = useState(categories[0]?.id ?? '')
  const [query, setQuery] = useState('')
  const [showCart, setShowCart] = useState(false)
  const [showChat, setShowChat] = useState(false)
  const [showService, setShowService] = useState(false)
  const [chatText, setChatText] = useState('')
  const [busy, setBusy] = useState(false)
  const [notice, setNotice] = useState('')

  const sessionId = guestSession?.sessionId
  const sessionCart = useMemo(() => cartItems.filter((item) => !sessionId || item.sessionId === sessionId), [cartItems, sessionId])
  const sessionMessages = useMemo(() => messages.filter((item) => !sessionId || item.sessionId === sessionId), [messages, sessionId])
  const sessionRequests = useMemo(() => requests.filter((item) => !sessionId || item.sessionId === sessionId), [requests, sessionId])
  const sessionOrders = useMemo(() => orders.filter((item) => item.tableNumber === tableNo && (!sessionId || item.sessionId === sessionId)), [orders, sessionId, tableNo])
  const latestOrder = sessionOrders[0]
  const total = sessionCart.reduce((sum, item) => sum + item.unitPrice * item.quantity, 0)
  const qty = sessionCart.reduce((sum, item) => sum + item.quantity, 0)
  const filtered = menuItems.filter((item) => item.available && (!activeCategory || item.categoryId === activeCategory) && (!query || `${item.name} ${item.description}`.toLowerCase().includes(query.toLowerCase())))

  async function join() {
    setBusy(true); setNotice('')
    try { await ensureGuestSession(tableNo, guestName.trim() || 'Guest'); setNotice(`Connected to Table ${tableNo}`) }
    catch (e) { setNotice(e instanceof Error ? e.message : 'Unable to join table.') }
    finally { setBusy(false) }
  }

  async function add(item: MenuItem) {
    if (!guestSession) { setNotice('Join the table first to start your shared order.'); return }
    try { await addToCart(item, guestSession.guestName); setNotice(`${item.name} added to the table.`) }
    catch (e) { setNotice(e instanceof Error ? e.message : 'Unable to add item.') }
  }

  async function placeOrder() {
    setBusy(true)
    try { await submitOrder(); setShowCart(false); setNotice('Order sent to the restaurant.') }
    catch (e) { setNotice(e instanceof Error ? e.message : 'Unable to submit order.') }
    finally { setBusy(false) }
  }

  async function request(type: ServiceRequest['type']) {
    try { await createServiceRequest(type); setShowService(false); setNotice(`${type === 'bill' ? 'Bill' : type} request sent.`) }
    catch (e) { setNotice(e instanceof Error ? e.message : 'Unable to send request.') }
  }

  async function sendChat(e: FormEvent) {
    e.preventDefault()
    if (!chatText.trim() || !guestSession) return
    const text = chatText.trim(); setChatText('')
    try { await sendMessage(text, guestSession.guestName, guestSession.sessionId) }
    catch (e) { setNotice(e instanceof Error ? e.message : 'Unable to send message.') }
  }

  return (
    <div className="guest-canvas" style={{ '--guest-primary': restaurant.primaryColor, '--guest-accent': restaurant.accentColor } as React.CSSProperties}>
      <div className="guest-app">
        <header className="guest-hero" style={{ backgroundImage: `linear-gradient(180deg,rgba(7,10,13,.34),rgba(7,10,13,.93)),url(${restaurant.coverUrl})` }}>
          <div className="guest-topline"><span className="guest-logo">AVA <b>XIN</b></span><button>EN <ChevronDown size={14} /></button></div>
          <div className="guest-hero-copy"><span className="guest-kicker">WELCOME TO</span><h1>{restaurant.name}</h1><p>{restaurant.tagline}</p></div>
          <div className="table-session-card">
            <div><span>TABLE</span><strong>{tableNo}</strong></div>
            <div><span>SESSION</span><strong className={guestSession ? 'live-dot' : ''}>{guestSession ? 'LIVE' : 'READY'}</strong></div>
            <div><span>GUEST</span><strong>{guestSession?.guestName ?? 'Not joined'}</strong></div>
          </div>
        </header>

        {!guestSession && (
          <section className="join-table-card">
            <div><UsersRound size={22} /><div><strong>Join Table {tableNo}</strong><p>Your selections stay attributed to you while everyone shares one live order.</p></div></div>
            <div className="join-row"><input value={guestName} onChange={(e) => setGuestName(e.target.value)} placeholder="Your name" /><button onClick={() => void join()} disabled={busy}>{busy ? 'Joining…' : 'Join table'}</button></div>
          </section>
        )}

        {latestOrder && (
          <section className="guest-order-status">
            <div><span>YOUR TABLE ORDER</span><StatusPill status={latestOrder.status} /></div>
            <strong>Order #{latestOrder.id.replace(/\D/g, '').slice(-4) || 'LIVE'}</strong>
            <p>{latestOrder.status === 'new' ? 'Sent to the restaurant. Waiting for confirmation.' : latestOrder.status === 'confirmed' ? 'Confirmed by the restaurant.' : latestOrder.status === 'preparing' ? 'Your order is being prepared.' : latestOrder.status === 'ready' ? 'Your order is ready.' : 'Order served.'}</p>
            <small>Elapsed {Math.floor(elapsedSince(latestOrder.createdAt) / 60)} min</small>
          </section>
        )}

        <div className="guest-actions-row">
          <button onClick={() => setShowChat(true)}><MessageCircle /><span>Chat</span>{sessionMessages.length > 1 && <i>{sessionMessages.length}</i>}</button>
          <button onClick={() => setShowService(true)}><Sparkles /><span>Service</span>{sessionRequests.some((r) => r.status === 'new') && <i>!</i>}</button>
          <button onClick={() => setShowCart(true)}><ReceiptText /><span>Shared order</span>{qty > 0 && <i>{qty}</i>}</button>
        </div>

        <section className="guest-menu-section">
          <div className="guest-menu-head"><div><span className="guest-kicker">OUR MENU</span><h2>Made for the table.</h2></div><label className="guest-search"><Search size={16} /><input value={query} onChange={(e) => setQuery(e.target.value)} placeholder="Search" /></label></div>
          <div className="category-scroller">
            {categories.filter((c) => c.active).map((category) => <button key={category.id} className={activeCategory === category.id ? 'active' : ''} onClick={() => setActiveCategory(category.id)}>{category.name}</button>)}
          </div>
          <div className="guest-menu-list">
            {filtered.map((item) => (
              <article className="guest-menu-card" key={item.id}>
                <img src={item.imageUrl} alt="" />
                <div className="guest-item-copy">
                  {item.featured && <span className="featured-label">SIGNATURE</span>}
                  <h3>{item.name}</h3>
                  <p>{item.description}</p>
                  <div><strong>{restaurant.currency} {item.price.toFixed(3)}</strong><button onClick={() => void add(item)}><Plus size={17} /> Add</button></div>
                </div>
              </article>
            ))}
          </div>
        </section>

        <footer className="guest-footer"><span>POWERED BY</span><strong>LAMSA TABLE</strong><small>{backendMode === 'demo' ? 'Demo experience' : 'Live restaurant session'}</small></footer>

        {qty > 0 && <button className="guest-floating-cart" onClick={() => setShowCart(true)}><span><b>{qty}</b> View shared order</span><strong>{restaurant.currency} {total.toFixed(3)}</strong></button>}
        {notice && <button className="guest-toast" onClick={() => setNotice('')}><Check size={16} />{notice}<X size={15} /></button>}
      </div>

      {showCart && <GuestSheet title="Shared Table Order" onClose={() => setShowCart(false)}>
        <div className="shared-order-intro"><UsersRound size={20} /><p>Everyone at Table {tableNo} sees this live. Items remain labelled by guest.</p></div>
        {sessionCart.length === 0 ? <EmptyState text="Nothing has been added yet." /> : <>
          {Object.entries(sessionCart.reduce<Record<string, typeof sessionCart>>((groups, item) => { (groups[item.guestName] ??= []).push(item); return groups }, {})).map(([name, items]) => <div className="guest-cart-group" key={name}><h4>{name}</h4>{items.map((item) => <div className="guest-cart-row" key={item.id}><div><strong>{item.name}</strong><span>{restaurant.currency} {(item.unitPrice * item.quantity).toFixed(3)}</span></div><div className="qty-control"><button onClick={() => void changeCartQty(item.id, item.quantity - 1)}><Minus size={14} /></button><b>{item.quantity}</b><button onClick={() => void changeCartQty(item.id, item.quantity + 1)}><Plus size={14} /></button></div></div>)}</div>)}
          <div className="guest-cart-total"><span>Total</span><strong>{restaurant.currency} {total.toFixed(3)}</strong></div>
          <button className="guest-primary-action" onClick={() => void placeOrder()} disabled={busy}>{busy ? 'Sending…' : 'Place Table Order'}</button>
          <p className="sheet-note">Any connected guest can submit the shared order. Chat messages never create billable items.</p>
        </>}
      </GuestSheet>}

      {showChat && <GuestSheet title={`${restaurant.name} Team`} onClose={() => setShowChat(false)}>
        <div className="chat-thread">
          {sessionMessages.length === 0 ? <EmptyState text="Start a conversation with the restaurant team." /> : sessionMessages.map((message) => <div className={`chat-bubble ${message.senderType}`} key={message.id}><span>{message.senderName}</span><p>{message.body}</p><small>{new Date(message.createdAt).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}</small></div>)}
        </div>
        <form className="chat-compose" onSubmit={sendChat}><input value={chatText} onChange={(e) => setChatText(e.target.value)} placeholder={guestSession ? 'Type a message…' : 'Join table to chat'} disabled={!guestSession} /><button disabled={!guestSession || !chatText.trim()}><Send size={18} /></button></form>
      </GuestSheet>}

      {showService && <GuestSheet title="How can we help?" onClose={() => setShowService(false)}>
        <div className="service-grid">
          {([['water','Water'],['bill','Bill'],['cutlery','Cutlery'],['napkins','Napkins'],['assistance','Staff assistance'],['other','Other']] as [ServiceRequest['type'], string][]).map(([type, label]) => <button key={type} disabled={!guestSession} onClick={() => void request(type)}><UtensilsCrossed size={21} /><strong>{label}</strong><span>Send request</span></button>)}
        </div>
        {!guestSession && <p className="sheet-note">Join the table first so the team knows where to respond.</p>}
      </GuestSheet>}
    </div>
  )
}

function GuestSheet({ title, onClose, children }: { title: string; onClose: () => void; children: React.ReactNode }) {
  return <div className="guest-sheet-backdrop" onMouseDown={onClose}><section className="guest-sheet" onMouseDown={(e) => e.stopPropagation()}><header><h3>{title}</h3><button onClick={onClose}><X /></button></header><div className="guest-sheet-body">{children}</div></section></div>
}

function EmptyState({ text }: { text: string }) { return <div className="empty-state"><span>◎</span><p>{text}</p></div> }
